import { OptionalAssets } from '../assets/assets';
import { SupportedCurrencies } from '../payment/payment';

export enum CheckoutEntryPath {
  NewOrder = 'new_order',
  ResumeExplicit = 'resume_explicit',
  ResumeImplicit = 'resume_implicit',
  OrderAgain = 'order_again',
}

export enum CheckoutSource {
  WelcomeSelectProgram = 'welcome_select_program',
  Welcome = 'welcome',
  MyPrograms = 'my_programs',
  WelcomeBanner = 'welcome_banner',
  CartWidget = 'cart_widget',
  OrdersPage = 'orders_page',
  OverviewBanner = 'overview_banner',
  QuickPurchase = 'quick_purchase',
}

export enum CheckoutContextState {
  Active = 'active',
  Terminated = 'terminated',
  None = 'none',
}

export interface MixpanelSession {
  checkoutEntryPath?: CheckoutEntryPath;
  isResumedSession?: boolean;
  checkoutSessionId?: string;
  checkoutSource?: CheckoutSource;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

// --------------------- PURCHASE EVENT PROPERTIES ---------------------

export interface AssetSelectedProperties {
  chosenAsset: OptionalAssets;
  cart_items_count: number;
  checkout_context_state: CheckoutContextState;
  checkout_session_id: string;
  checkout_source?: CheckoutSource;
}

export interface GroupSelectedProperties {
  groupName: string;
  asset: OptionalAssets;
  cart_items_count: number;
  checkout_context_state: CheckoutContextState;
  total_amount_usd?: number;
  total_amount_local?: number;
  currency?: SupportedCurrencies;
}

export interface CheckoutClickedBackProperties {
  checkout_session_id: string | null;
  checkout_entry_path: CheckoutEntryPath | null;
  checkout_context_state: CheckoutContextState;
  cart_items_count: number;
  current_step: EventTypes;
  target_step: EventTypes;
  stepProperties: Record<string, string | number | boolean | undefined>;
}

export interface CheckoutClickedDeleteCartProperties {
  checkout_session_id: string | null;
  checkout_entry_path: CheckoutEntryPath | null;
  checkout_context_state: CheckoutContextState;
  checkout_source: CheckoutSource | null;
  cart_items_count: number;
  cartProperties: {
    items_count: number;
    total_amount_local: number;
    total_amount_usd: number;
    currency: SupportedCurrencies;
    asset?: OptionalAssets;
    group_name?: string;
    buying_power?: number;
    trading_system?: string;
  };
}

export interface CartOpenedProperties {
  [key: string]: string | number | boolean | undefined;
}

export interface BannerClickedProperties {
  checkout_session_id: string;
  checkout_entry_path: CheckoutEntryPath;
  checkout_source: CheckoutSource;
  banner_id: string;
  banner_text: string;
  destination_url: string;
  device: 'desktop' | 'mobile';
  brand: string;
  program_id?: string;
}

export interface CheckoutAddToCartProperties {
  asset: OptionalAssets;
  group_name: string;
  buying_power: number;
  trading_system: string;
  total_amount_usd: number;
  total_amount_local: number;
  currency: SupportedCurrencies;
  cart_items_count: number;
  checkout_entry_path: CheckoutEntryPath;
  checkout_session_id: string | null;
  checkout_source: CheckoutSource | null;
  checkout_context_state: CheckoutContextState;
}

export interface CheckoutAddToCartFailedProperties
  extends CheckoutAddToCartProperties {
  error_type: string;
}

export interface CheckoutOrderAgainInitiatedProperties {
  checkout_session_id: string;
  checkout_entry_path: CheckoutEntryPath;
  checkout_context_state: CheckoutContextState;
  checkout_source: CheckoutSource;
  cart_items_count: number;
  asset: OptionalAssets;
  group_name: string;
  buying_power: number;
  trading_system: string;
  total_amount_local: number;
  total_amount_usd: number;
  currency: SupportedCurrencies;
}

export interface CheckoutCouponEventProperties {
  checkout_session_id: string;
  checkout_entry_path: CheckoutEntryPath;
  checkout_source: CheckoutSource;
  coupon_name: string;
  is_success: boolean;
}

export type PurchaseEventProperties =
  | AssetSelectedProperties
  | GroupSelectedProperties
  | CheckoutClickedBackProperties
  | CheckoutClickedDeleteCartProperties
  | CartOpenedProperties
  | BannerClickedProperties
  | CheckoutAddToCartProperties
  | CheckoutAddToCartFailedProperties
  | CheckoutOrderAgainInitiatedProperties
  | CheckoutCouponEventProperties;

export enum EventTypes {
  // --------------------- CLIENT EVENTS ---------------------
  SIGN_IN = 'sign_in',
  SIGN_UP = 'sign_up',
  VIEW_ALL_ARTICLES = 'view_all_articles',
  ARTICLE_VIEW = 'article_view',
  WATCH_TUTORIAL_VIDEO = 'watch_tutorial_video',
  PAGE_VIEW = 'page_view',
  MODAL_CLOSED = 'modal_closed',
  ORDER_SELECT_GROUP = 'order_select_group',
  ORDER_ACCOUNT_PROGRAM = 'order_account_program',
  ORDER_CURRENT_STEP = 'order_current_step',
  // --------------------- WITHDRAW EVENTS ---------------------
  WITHDRAW_MODAL_OPENED = 'withdraw_modal_opened',
  WITHDRAW_ELIGIBILITY_CHECKED = 'withdraw_eligibility_checked',
  WITHDRAW_PAID = 'withdraw_paid',
  MAIL_OTP_SENT = 'mail_otp_sent',
  // --------------------- CHECKOUT EVENTS ---------------------
  CHECKOUT_ORDER_CLICKED = 'checkout_clicked_new_order',
  CHECKOUT_ORDER_AGAIN_INITIATED = 'checkout_order_again_initiated',
  CHECKOUT_RESUME_CLICKED = 'checkout_resume_clicked',
  CHECKOUT_ASSET_SELECTED = 'checkout_selected_asset',
  CHECKOUT_GROUP_SELECTED = 'checkout_selected_group',
  TWIN_PROGRAM_SWITCHED = 'twin_program_switched',
  CHECKOUT_CLICKED_ADD_TO_CART = 'checkout_clicked_add_to_cart',
  CHECKOUT_ADD_TO_CART = 'checkout_cart_added',
  CHECKOUT_ADD_TO_CART_FAILED = 'checkout_add_to_cart_failed',
  CHECKOUT_CLICKED_BANNER = 'checkout_clicked_banner',
  CHECKOUT_CLICKED_PROCEED_TO_BILLING = 'checkout_clicked_proceed_to_billing',
  CHECKOUT_CLICKED_BACK = 'checkout_clicked_back',
  CHECKOUT_CLICKED_DELETE_CART = 'checkout_clicked_delete_cart',
  CHECKOUT_CART_DELETED = 'checkout_cart_deleted',
  CHECKOUT_PURCHASE_INITIATED = 'checkout_clicked_initiate_payment',
  CHECKOUT_PURCHASE_COMPLETED = 'checkout_payment_completed',
  CHECKOUT_COUPON_ADD = 'checkout_coupon_add',
  CHECKOUT_COUPON_SELECT = 'checkout_coupon_select',
  CHECKOUT_PURCHASE_INITIATED_FAILED = 'checkout_initiate_payment_failed',
  MARKETING_CONSENT_CHANGED = 'marketing_consent_changed',
  // --------------------- LIVE CHAT EVENTS ---------------------
  OPEN_LIVE_CHAT = 'open_live_chat',
  CART_OPENED = 'cart_opened',
  // --------------------- TRADETALK EVENTS ---------------------
  ENTERED_TRADINGTALK_PUBLIC = 'entered_tradingtalk_public',
  TRADETALK_POST_CREATED = 'tradetalk_post_created',

  TRADETALK_COMMENT = 'tradetalk_comment',
  // --------------------- TRADETALK NAVIGATION EVENTS ---------------------
  TRADETALK_FILTER = 'tradetalk_filter',
  TRADETALK_CLICK_MY_TALKS = 'tradetalk_click_my_talks',
  TRADETALK_NEW_POST = 'tradetalk_new_post',
  TRADETALK_NEXT = 'tradetalk_next',
  TRADETALK_DISCARD = 'tradetalk_discard',
  TRADETALK_PUBLISH = 'tradetalk_publish',
  TRADETALK_SAVE = 'tradetalk_save',
  TRADETALK_DELETE = 'tradetalk_delete',
  TRADETALK_SHARE = 'tradetalk_share',
  TRADETALK_LIKE = 'tradetalk_like',
  TRADETALK_LIKE_COMMENT = 'tradetalk_like_comment',
  TRADETALK_DISLIKE_COMMENT = 'tradetalk_dislike_comment',
  TRADETALK_COMMENT_COMMENT = 'tradetalk_comment_comment',
  TRADETALK_CHANGE_VIEW = 'tradetalk_change_view',
  TRADETALK_HOW_DID_AI_DO_RESPOND = 'tradetalk_how_did_ai_do_respond',
  TRADETALK_OPEN_POST = 'tradetalk_open_post',
  TRADETALK_SEARCH = 'tradetalk_search',
  TRADETALK_COMMENT_CLICKED = 'tradetalk_comment_clicked',
  TRADETALK_ANALYZE = 'tradetalk_analyze',
  // --------------------- TRADETALK AI EVENTS ---------------------
  TTAI_CLICK_GET_INSIGHT = 'TTai_click_get_insight',
  TTAI_CLICK_DISCARD = 'TTai_click_discard',
  TTAI_CLICK_PUBLISH = 'TTai_click_publish',
  TTAI_CLICK_REFRESH = 'TTai_click_refresh',
  TTAI_CLICK_OPEN_POST_ASSISTANT = 'TTai_click_open_post_assistant',
  // --------------------- TRADE SCORE EVENTS ---------------------
  TRADE_SCORE_VIEWED = 'tradescore_viewed',
  TRADE_SCORE_TOOLTIP_CLICKED = 'tradescore_tooltip_clicked',
  TRADE_SCORE_CTA_CLICKED = 'tradescore_cta_clicked',
  // --------------------- AFFILIATE EVENTS ---------------------
  AFFILIATE_CTA_CLICKED = 'affiliate_cta_clicked',
  AFFILIATE_TERMS_ACCEPTED = 'affiliate_terms_accepted',
  AFFILIATE_COPY_LINK_CLICKED = 'affiliate_copy_link_clicked',
  AFFILIATE_COPY_COUPON_CLICKED = 'affiliate_copy_coupon_clicked',
  AFFILIATE_KIT_CLICKED = 'affiliate_kit_clicked',
  // --------------------- QUICK PURCHASE EVENTS ---------------------
  QUICK_PURCHASE_OTP_SENT = 'quick_purchase_otp_sent',
  QUICK_PURCHASE_OTP_VERIFIED = 'quick_purchase_otp_verified',
  QUICK_PURCHASE_OTP_FAILED = 'quick_purchase_otp_failed',
  QUICK_PURCHASE_OTP_MAX_RETRIES = 'quick_purchase_otp_max_retries',
  QUICK_PURCHASE_SESSION_CREATED = 'quick_purchase_session_created',
  QUICK_PURCHASE_CART_READY = 'quick_purchase_cart_ready',
  QUICK_PURCHASE_ERROR = 'quick_purchase_error',
  // --------------------- SERVER EVENTS ---------------------
  AUTH_SERVICE_REGISTER = 'auth_service_register',
  CONTEST_SIGNUP_REGISTRATION_COMPLETED = 'contest_signup_registration_completed',
  PROFILE_NICKNAME_CHANGE = 'profile_nickname_change',
  MT_POSITION_MANAGER_ITERATION = 'meta_trader_publisher',
  CT_POSITION_MANAGER_ITERATION = 'c_trader_publisher',
  TE_POSITION_MANAGER_ITERATION = 'trader_evolution_publisher',
  PROGRAM_CONCLUDED = 'program_concluded',
}

export enum CheckoutErrorType {
  CartItemLimitExceeded = 'cart_item_limit_exceeded',
  CountryNotAllowed = 'country_not_allowed',
  UserBlacklisted = 'user_blacklisted',
  UnknownError = 'unknown_error',
}
